import React from "react";
import {
  Icon,
  Button,
  Avatar,
  AvatarFallback,
  Checkbox,
} from "@elaniintc/goes-design-system";
import type { FieldType } from "@/schemas/digital-files";
import { cn } from "@/utils/theme";

interface FieldCardProps {
  field: FieldType;
  sectionId: string;
  readonly?: boolean;
  fieldInfo: { label: string; icon: string };
  onDeleteField?: (sectionId: string, fieldId: string) => void;
  onEditField?: (sectionId: string, field: FieldType) => void;
  onToggleShowInSummary?: (
    sectionId: string,
    fieldId: string,
    value: boolean,
  ) => void;
  className?: string;
  error?: string;
}

const FieldCard: React.FC<FieldCardProps> = ({
  field,
  fieldInfo,
  onDeleteField,
  sectionId,
  onEditField,
  onToggleShowInSummary,
  readonly = false,
  className,
  error,
}) => {
  return (
    <div
      className={cn(
        "flex items-center flex-col gap-3 p-2 border-neutral-quiet rounded-lg border",
        className,
      )}
    >
      <div className="flex items-center gap-3 w-full">
        {readonly ? (
          <Avatar shape="square" size="xs">
            <AvatarFallback
              shape="square"
              color="neutral"
              className="bg-neutral-object-accent-silent"
            >
              <Icon
                type="far"
                name={fieldInfo.icon}
                size="xs"
                className="text-body"
              />
            </AvatarFallback>
          </Avatar>
        ) : (
          <Icon type="far" name={fieldInfo.icon} className="text-body p-1" />
        )}
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-body truncate text-base">
            {field.name || "Nombre del campo"}
          </div>
          <div className="text-sm text-body-quiet">{fieldInfo.label}</div>
        </div>
        {!readonly && (
          <>
            <Button
              variant="ghost"
              className="w-8 h-8"
              onClick={() => onEditField && onEditField(sectionId, field)}
              aria-label="Editar campo"
            >
              <Icon type="far" name="pen" />
            </Button>
            <Button
              variant="ghost"
              className="w-8 h-8"
              onClick={() =>
                field.id && onDeleteField && onDeleteField(sectionId, field.id)
              }
              aria-label="Eliminar campo"
            >
              <Icon type="far" name="close" />
            </Button>
          </>
        )}

        {readonly && onToggleShowInSummary && (
          <div className="flex items-center gap-1.5">
            <p className="text-body leading-2xl text-size-body-sm">
              Mostrar a usuario externo
            </p>
            <Checkbox
              checked={field.configuration?.show_in_summary || false}
              onChange={(e) => {
                if (onToggleShowInSummary && field.id) {
                  onToggleShowInSummary(sectionId, field.id, e.target.checked);
                }
              }}
            />
          </div>
        )}
      </div>
      {!readonly && error && (
        <span className="text-size-body-xs w-full text-danger">
          <Icon type="far" name="circle-xmark" className="mr-1.5" />
          {error}
        </span>
      )}
    </div>
  );
};

export default FieldCard;
